import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getWedding } from "../api/weddings";
import { Wedding } from "../types";
import { formatDate } from "../utils/format";
import Loading from "../components/Loading";

const googleCalendarUrl = (wedding: Wedding): string => {
  const start = new Date(wedding.date);
  const end = new Date(start.getTime() + 4 * 60 * 60 * 1000);

  const fmt = (d: Date) =>
    d
      .toISOString()
      .replace(/[-:]/g, "")
      .replace(/\.\d{3}Z$/, "Z");

  const title = encodeURIComponent(wedding.title);
  const location = encodeURIComponent(
    [wedding.venue_name, wedding.location, wedding.city, wedding.state]
      .filter(Boolean)
      .join(", ")
  );
  const details = encodeURIComponent(
    wedding.story || wedding.description || "Ticketed Wedding event"
  );

  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(start)}/${fmt(end)}&details=${details}&location=${location}`;
};

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const weddingId = searchParams.get("wedding_id");
  const rsvpId = searchParams.get("rsvp_id");
  const [wedding, setWedding] = useState<Wedding | null>(null);
  const [loading, setLoading] = useState(!!weddingId);

  useEffect(() => {
    if (!weddingId) return;
    let cancelled = false;
    setLoading(true);
    getWedding(weddingId)
      .then(({ data }) => {
        if (!cancelled) setWedding(data);
      })
      .catch(() => {
        if (!cancelled) setWedding(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [weddingId]);

  if (loading) return <Loading />;

  const managePath =
    weddingId && rsvpId ? `/weddings/${weddingId}/rsvp/${rsvpId}` : null;

  return (
    <div className="page">
      <div className="success-message centered">
        <h1>Payment Successful</h1>
        <p>Thank you! Your ticket has been confirmed.</p>

        {wedding && (
          <div style={{ margin: "1.5rem 0", textAlign: "left" }}>
            <h3 style={{ marginBottom: "0.5rem" }}>{wedding.title}</h3>
            <p className="wedding-date">{formatDate(wedding.date)}</p>
            {(wedding.venue_name || wedding.location) && (
              <p className="wedding-location">
                {[wedding.venue_name, wedding.location].filter(Boolean).join(" · ")}
              </p>
            )}
            <a
              href={googleCalendarUrl(wedding)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary btn-sm"
              style={{ marginTop: "1rem" }}
            >
              Add to Google Calendar
            </a>
          </div>
        )}

        <div className="button-row" style={{ justifyContent: "center" }}>
          {managePath && (
            <Link to={managePath} className="btn btn-primary">
              Return to your RSVP
            </Link>
          )}
          {wedding && (
            <Link to={`/weddings/${wedding.id}`} className="btn btn-secondary">
              View Event
            </Link>
          )}
          <Link to="/" className="btn btn-secondary">
            Back Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
